<script lang="ts">
  import clsx from 'clsx';
  import { generateUniqueElementId } from '@athena-lib/unique-element-id';
  import { objectEntries } from '@athena-lib/object-utils';
  import { type Responsive } from '@athena-lib/responsive-utils';
  import { lightColors, getColorContext } from '@athena-lib/container-context.svelte';
  import { ArrowDropDown } from '@athena-lib/Icons';
  import FieldLabel from '../FieldLabel/FieldLabel.svelte';
  import type { DropdownProps } from './types';
  import { normalizeOptions } from './utils';

  const colorContext = getColorContext();

  let {
    options,
    placeholder,
    value = $bindable(),
    id,
    label,
    variant = 'solid',
    visuallyHiddenLabel,
    class: className,
    optionClassName,
    innerClassName,
    onChange,
  }: DropdownProps = $props();
  const normalizedOptions = $derived(normalizeOptions(options));
  const idToUse = id ?? generateUniqueElementId();

  const responsiveBorderColor = $derived.by(() => {
    const contextBackground = colorContext.value;
    const responsiveBorderObj: Responsive<string> = {
      lg: lightColors.includes(contextBackground.lg)
        ? 'lg:border-st-gray-200'
        : 'lg:border-st-white',
      md: lightColors.includes(contextBackground.md)
        ? 'md:border-st-gray-200'
        : 'md:border-st-white',
      sm: lightColors.includes(contextBackground.sm) ? 'border-st-gray-200' : 'border-st-white',
    };

    return objectEntries(responsiveBorderObj)
      .map(([_, value]) => value)
      .join(' ');
  });
</script>

<div class={clsx(className)}>
  {#if label}
    <FieldLabel
      inputId={idToUse}
      {label}
      class="pb-1 font-semibold"
      visuallyHidden={visuallyHiddenLabel}
    />
  {/if}
  <div
    class={clsx(
      innerClassName,
      variant === 'ghost' && 'border bg-transparent',
      variant === 'ghost' && responsiveBorderColor,
      variant === 'solid' && 'bg-white',
      'flex w-full justify-between rounded-md border py-2 pl-3 pr-2 ring-offset-1 has-[:focus]:ring has-[:focus]:ring-st-blue-500',
    )}
  >
    <select
      {placeholder}
      bind:value
      id={idToUse}
      onchange={(e) => {
        if (onChange) {
          const selectedOption = normalizedOptions.find((o) => o.key === e.currentTarget.value);
          if (selectedOption) {
            onChange(selectedOption.key, selectedOption.value);
          }
        }
      }}
      class="w-full appearance-none bg-transparent outline-none"
    >
      {#if placeholder}
        <option
          disabled
          selected={!normalizedOptions.find((o) => o.key === value)}
          value=""
          class={optionClassName}
        >
          {placeholder}
        </option>
      {/if}
      {#each normalizedOptions as option (option.key)}
        <option
          value={option.key}
          selected={option.key === value}
          disabled={option.disabled}
          class={optionClassName}
        >
          {option.value}
        </option>
      {/each}
    </select>
    <ArrowDropDown sizeClass="w-5 aspect-square" />
  </div>
</div>
